import {
  ActionIcon,
  Badge,
  Box,
  Group,
  Loader,
  Popover,
  ScrollArea,
  Text,
  TextInput,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconCheck,
  IconPlus,
  IconSearch,
  IconTrash,
} from "@tabler/icons-react";
import { useMemo, useState } from "react";
import "./orderTagPicker.css";
import { repositoryPos } from "../../../_base/_const/_constVar";
import { MessageResponse } from "../../../model/MessageResponse";
import {
  OrderTag,
  TAG_API,
  TAG_STATUS,
  TblDmTag,
  TblDmTagCategory,
  readPagedList,
} from "../../../model/TblDmTag";

/** Nhóm thẻ theo danh mục, dùng chung cho mọi ô trên trang */
export type OrderTagGroup = {
  categoryId: string;
  categoryName: string;
  allowMultiple: boolean;
  inactive: boolean;
  tags: { id: string; tagName: string; color: string | null; inactive: boolean }[];
};

// Danh mục thẻ áp dụng cho đơn hàng
const ORDER_OBJECT_TYPE = "ORDER";

/** Tải danh mục + thẻ của đơn hàng (gọi 1 lần cho cả trang) */
export const loadOrderTagGroups = async (): Promise<OrderTagGroup[]> => {
  const [catRes, tagRes] = await Promise.all([
    repositoryPos.get<MessageResponse<any>>(
      `${TAG_API.listCategory}?Type=${ORDER_OBJECT_TYPE}&Skip=0&Take=1000`,
      true,
    ),
    repositoryPos.get<MessageResponse<any>>(
      `${TAG_API.listTag}?Type=${ORDER_OBJECT_TYPE}&Skip=0&Take=1000`,
      true,
    ),
  ]);
  const categories = catRes?.success
    ? readPagedList<TblDmTagCategory>(catRes.data).list
    : [];
  const tags = tagRes?.success ? readPagedList<TblDmTag>(tagRes.data).list : [];

  return categories
    .map((c) => ({
      categoryId: c.id,
      categoryName: c.categoryName,
      allowMultiple: c.allowMultiple,
      inactive: c.status === TAG_STATUS.inactive,
      tags: tags
        .filter((t) => t.categoryId === c.id)
        .map((t) => ({
          id: t.id,
          tagName: t.tagName,
          color: t.color,
          inactive:
            t.status === TAG_STATUS.inactive || c.status === TAG_STATUS.inactive,
        })),
    }))
    .filter((g) => g.tags.length > 0)
    .sort((a, b) => a.categoryName.localeCompare(b.categoryName, "vi"));
};

export const colorVar = (color: string | null) =>
  `var(--mantine-color-${color || "blue"}-6)`;

export const Dot = ({ color }: { color: string | null }) => (
  <Box
    component="span"
    w={8}
    h={8}
    style={{ borderRadius: "50%", background: colorVar(color), flexShrink: 0 }}
  />
);

/** Chip thẻ có chấm màu, dùng cho popup gắn thẻ và ô lọc theo thẻ */
export const TagChip = ({
  name,
  color,
  checked,
  inactive,
  title,
  onClick,
}: {
  name: string;
  color: string | null;
  checked: boolean;
  inactive?: boolean;
  title?: string;
  onClick: () => void;
}) => (
  <UnstyledButton
    role="menuitemcheckbox"
    aria-checked={checked}
    title={title}
    onClick={onClick}
    className="order-tag-picker-chip"
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      height: 30,
      padding: "0 10px",
      borderRadius: 999,
      fontSize: 13,
      border: `1px solid ${checked ? colorVar(color) : "var(--mantine-color-gray-3)"}`,
      background: checked
        ? `var(--mantine-color-${color || "blue"}-light)`
        : "#fff",
      color: checked ? `var(--mantine-color-${color || "blue"}-light-color)` : undefined,
      fontWeight: checked ? 600 : 400,
    }}
  >
    <Dot color={color} />
    <span>
      {name}
      {inactive ? " (ngừng dùng)" : ""}
    </span>
    {checked ? <IconCheck size={14} stroke={2.4} color={colorVar(color)} /> : null}
  </UnstyledButton>
);

const OrderTagPicker = ({
  orderId,
  orderNumber,
  value,
  groups,
  onChange,
}: OrderTagPickerProps) => {
  const [opened, { toggle, close }] = useDisclosure(false);
  const [keySearch, setKeySearch] = useState("");
  const [saving, setSaving] = useState(false);

  const selectedIds = useMemo(() => new Set(value.map((x) => x.id)), [value]);

  // Chỉ hiện thẻ đang dùng; thẻ đã gắn nhưng đã ngừng dùng vẫn hiện để còn bỏ được
  const visibleGroups = useMemo(
    () =>
      groups
        .map((g) => ({
          ...g,
          tags: g.tags.filter((t) => !t.inactive || selectedIds.has(t.id)),
        }))
        .filter((g) => g.tags.length > 0),
    [groups, selectedIds],
  );

  const searchResults = useMemo(() => {
    const key = keySearch.trim().toLowerCase();
    if (!key) return [];
    return visibleGroups.flatMap((g) =>
      g.tags
        .filter((t) => t.tagName.toLowerCase().includes(key))
        .map((t) => ({ group: g, tag: t })),
    );
  }, [visibleGroups, keySearch]);

  const save = async (next: OrderTag[]) => {
    const previous = value;
    onChange(next); // cập nhật ngay trên bảng, lỗi thì trả lại như cũ
    setSaving(true);
    const res = await repositoryPos.post<MessageResponse<boolean>>(
      TAG_API.assignOrderTags,
      { orderId, tagIds: next.map((x) => x.id) },
    );
    setSaving(false);
    if (!res?.success) onChange(previous);
  };

  const toggleTag = (group: OrderTagGroup, tag: OrderTagGroup["tags"][number]) => {
    if (saving) return;
    if (selectedIds.has(tag.id)) {
      save(value.filter((x) => x.id !== tag.id));
      return;
    }
    const item: OrderTag = {
      id: tag.id,
      tagName: tag.tagName,
      categoryId: group.categoryId,
      categoryName: group.categoryName,
      color: tag.color,
      status: tag.inactive ? TAG_STATUS.inactive : TAG_STATUS.active,
    };
    // Danh mục chỉ 1 thẻ: chọn thẻ mới thì bỏ thẻ cũ cùng danh mục
    const kept = group.allowMultiple
      ? value
      : value.filter((x) => x.categoryId !== group.categoryId);
    save([...kept, item]);
  };

  const renderTagRow = (
    group: OrderTagGroup,
    tag: OrderTagGroup["tags"][number],
    showCategory: boolean,
  ) => {
    const checked = selectedIds.has(tag.id);
    return (
      <TagChip
        key={tag.id}
        name={tag.tagName}
        color={tag.color}
        checked={checked}
        inactive={tag.inactive}
        title={showCategory ? group.categoryName : undefined}
        onClick={() => toggleTag(group, tag)}
      />
    );
  };

  return (
    <Popover
      opened={opened}
      onChange={(o) => {
        if (!o) {
          close();
          setKeySearch("");
        }
      }}
      position="bottom-start"
      shadow="md"
      radius="md"
      width={380}
      trapFocus
    >
      <Popover.Target>
        <UnstyledButton
          aria-label={`Thẻ của đơn ${orderNumber}`}
          onClick={(e) => {
            e.stopPropagation();
            toggle();
          }}
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 4,
            maxWidth: "100%",
          }}
        >
          {value.map((tag) => (
              <Badge
                key={tag.id}
                variant="light"
                radius="sm"
                tt="none"
                fw={500}
                color={tag.color || "blue"}
                title={tag.categoryName ?? undefined}
                leftSection={<Dot color={tag.color} />}
              >
                {tag.tagName}
              </Badge>
          ))}
          <Box
            component="span"
            title="Gắn thẻ"
            style={{
              width: 22,
              height: 22,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              border: "1px solid var(--mantine-color-gray-4)",
              background: "#fff",
              color: "var(--mantine-color-gray-7)",
            }}
          >
            <IconPlus size={12} stroke={2.4} />
          </Box>
        </UnstyledButton>
      </Popover.Target>

      <Popover.Dropdown p={8} onClick={(e) => e.stopPropagation()}>
        <Group gap={6} wrap="nowrap" mb={6}>
          <TextInput
            style={{ flex: 1 }}
            size="sm"
            placeholder="Tìm thẻ đơn hàng"
            rightSection={
              saving ? <Loader size={14} /> : <IconSearch size={16} />
            }
            value={keySearch}
            onChange={(e) => setKeySearch(e.currentTarget.value)}
            data-autofocus
          />
          <Tooltip label="Bỏ tất cả thẻ" withinPortal={false}>
            <ActionIcon
              variant="default"
              size={36}
              aria-label="Bỏ tất cả thẻ"
              disabled={!value.length || saving}
              onClick={() => save([])}
            >
              <IconTrash size={18} stroke={1.6} />
            </ActionIcon>
          </Tooltip>
        </Group>

        {keySearch.trim() ? (
          <ScrollArea.Autosize mah={320} type="auto">
            {searchResults.length ? (
              <Group gap={6} px={10} py={4}>
                {searchResults.map(({ group, tag }) => renderTagRow(group, tag, true))}
              </Group>
            ) : (
              <Text size="sm" c="dark.3" ta="center" py="md">
                Không tìm thấy thẻ
              </Text>
            )}
          </ScrollArea.Autosize>
        ) : visibleGroups.length ? (
          <ScrollArea.Autosize mah={360} type="auto">
            {visibleGroups.map((g, idx) => (
              <Box key={g.categoryId} mt={idx === 0 ? 0 : 6}>
                <Group gap={6} px={10} pt={4} pb={2}>
                  <Text size="xs" fw={700} tt="uppercase" c="dark.4">
                    {g.categoryName}
                  </Text>
                  {!g.allowMultiple ? (
                    <Text size="xs" c="dark.3">
                      (chọn 1)
                    </Text>
                  ) : null}
                </Group>
                <Group gap={6} px={10} pt={4} pb={4}>
                  {g.tags.map((t) => renderTagRow(g, t, false))}
                </Group>
              </Box>
            ))}
          </ScrollArea.Autosize>
        ) : (
          <Text size="sm" c="dark.3" ta="center" py="md">
            Chưa có thẻ cho đơn hàng. Tạo ở Danh mục dữ liệu › Cấu hình tag.
          </Text>
        )}
      </Popover.Dropdown>
    </Popover>
  );
};

export default OrderTagPicker;

type OrderTagPickerProps = {
  orderId: number;
  orderNumber: string;
  value: OrderTag[];
  groups: OrderTagGroup[];
  onChange: (tags: OrderTag[]) => void;
};
